<template>
  <div
    v-if="banners.length"
    class="banner-slider"
    @mouseenter="pauseAutoplay"
    @mouseleave="resumeAutoplay"
  >
    <div class="banner-slider__viewport">
      <div
        class="banner-slider__track"
        :style="trackStyle"
        @touchstart="onTouchStart"
        @touchend="onTouchEnd"
      >
        <a
          v-for="(banner, index) in banners"
          :key="index"
          :href="banner.link"
          target="_blank"
          rel="noopener noreferrer"
          class="banner-slider__slide"
          @click="onSlideClick"
        >
          <img :src="banner.image" alt="" class="banner-slider__image" />
        </a>
      </div>
    </div>

    <div v-if="hasMultiple" class="banner-slider__controls">
      <button
        type="button"
        class="banner-slider__arrow banner-slider__arrow--prev"
        :aria-label="$t('BANNER.SLIDER.PREV')"
        @click="prev"
      >
        <ArrowLeftIcon />
      </button>

      <div class="banner-slider__dots">
        <button
          v-for="(banner, index) in banners"
          :key="index"
          type="button"
          class="banner-slider__dot"
          :class="{ 'banner-slider__dot--active': index === activeIndex }"
          :aria-label="`${index + 1}`"
          :aria-current="index === activeIndex ? 'true' : null"
          @click="goTo(index)"
        />
      </div>

      <button
        type="button"
        class="banner-slider__arrow banner-slider__arrow--next"
        :aria-label="$t('BANNER.SLIDER.NEXT')"
        @click="next"
      >
        <ArrowLeftIcon />
      </button>
    </div>
  </div>
</template>

<script>
import ArrowLeftIcon from '@/assets/icons/arrow-left.svg';

const SWIPE_THRESHOLD = 40;

export default {
  name: 'BannerSlider',

  components: {
    ArrowLeftIcon,
  },

  props: {
    banners: {
      type: Array,
      required: true,
    },
    autoplay: {
      type: Boolean,
      default: true,
    },
    interval: {
      type: Number,
      default: 5000,
    },
  },

  data() {
    return {
      activeIndex: 0,
      isRtl: false,
      isPaused: false,
      autoplayTimer: null,
      touchStartX: null,
      swiped: false,
    };
  },

  computed: {
    hasMultiple() {
      return this.banners.length > 1;
    },
    trackStyle() {
      const sign = this.isRtl ? 1 : -1;
      return { transform: `translateX(${sign * this.activeIndex * 100}%)` };
    },
  },

  watch: {
    banners() {
      this.setIndex(this.activeIndex);
      this.startAutoplay();
    },
  },

  mounted() {
    if (typeof document !== 'undefined') {
      this.isRtl = document.querySelector('body').dir === 'rtl';
    }
    this.startAutoplay();
  },

  beforeDestroy() {
    this.stopAutoplay();
  },

  methods: {
    setIndex(index) {
      const { length } = this.banners;
      if (!length) return;
      this.activeIndex = ((index % length) + length) % length;
    },
    next() {
      this.setIndex(this.activeIndex + 1);
      this.restartAutoplay();
    },
    prev() {
      this.setIndex(this.activeIndex - 1);
      this.restartAutoplay();
    },
    goTo(index) {
      this.setIndex(index);
      this.restartAutoplay();
    },
    startAutoplay() {
      this.stopAutoplay();
      if (this.autoplay && this.hasMultiple && !this.isPaused) {
        this.autoplayTimer = setInterval(
          () => this.setIndex(this.activeIndex + 1),
          this.interval
        );
      }
    },
    stopAutoplay() {
      clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    },
    restartAutoplay() {
      this.startAutoplay();
    },
    pauseAutoplay() {
      this.isPaused = true;
      this.stopAutoplay();
    },
    resumeAutoplay() {
      this.isPaused = false;
      this.startAutoplay();
    },
    onTouchStart(event) {
      this.touchStartX = event.changedTouches[0].clientX;
      this.swiped = false;
    },
    onTouchEnd(event) {
      if (this.touchStartX === null) return;
      const deltaX = event.changedTouches[0].clientX - this.touchStartX;
      this.touchStartX = null;
      if (Math.abs(deltaX) <= SWIPE_THRESHOLD) return;
      this.swiped = true;
      const goForward = this.isRtl ? deltaX > 0 : deltaX < 0;
      if (goForward) {
        this.next();
      } else {
        this.prev();
      }
    },
    onSlideClick(event) {
      // prevent opening the link when the tap was actually a swipe
      if (this.swiped) {
        event.preventDefault();
        this.swiped = false;
      }
    },
  },
};
</script>

<style lang="scss">
.banner-slider {
  position: relative;
  width: 100%;

  &__viewport {
    overflow: hidden;
    border-radius: 6px;
    aspect-ratio: 1120 / 240;
  }

  &__track {
    display: flex;
    height: 100%;
    transition: transform 0.5s ease;
    will-change: transform;
  }

  &__slide {
    flex: 0 0 100%;
    width: 100%;
    height: 100%;
    display: block;
    transition: opacity 0.3s ease;

    &:hover {
      opacity: 0.9;
    }
  }

  &__image {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  // controls live in the bottom 56px "safe" band of the banner
  &__controls {
    position: absolute;
    left: 0;
    right: 0;
    bottom: 3.5%;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 14px;
    pointer-events: none;
  }

  &__arrow {
    pointer-events: auto;
    flex: none;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 34px;
    height: 34px;
    padding: 0;
    border: none;
    border-radius: 50%;
    background: #fff;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
    opacity: 0.5;
    cursor: pointer;
    transition: background 0.2s ease, transform 0.1s ease;

    &:hover {
      background: #fff;
      opacity: 0.9;
    }

    &:active {
      transform: scale(0.94);
    }

    svg {
      width: 13px;
      height: 13px;
      fill: $lb-medium-black;
    }

    // arrow-left.svg points left by default (used as-is for "prev")
    @include ltr {
      &--next svg {
        transform: rotate(180deg);
      }
    }

    @include rtl {
      &--prev svg {
        transform: rotate(180deg);
      }
    }
  }

  &__dots {
    pointer-events: auto;
    display: flex;
    align-items: center;
    gap: 6px;
  }

  &__dot {
    pointer-events: auto;
    width: 6px;
    height: 6px;
    padding: 0;
    border: none;
    border-radius: 4px;
    background: rgba(255, 255, 255, 0.55);
    cursor: pointer;
    transition: width 0.3s ease, background 0.3s ease;

    &--active {
      width: 20px;
      background: #fff;
    }
  }
}
</style>
